import filesize from 'filesize'
import * as React from 'react'
import {useSelector} from 'react-redux'

import {FileUpload} from 'airship/components/FileUpload'
import {FieldValidation, ValidationScope} from 'airship/components/Validator'
import Select from 'airship/components/Validator/Select'
import TextareaInput from 'airship/components/Validator/TextareaInput'
import TextInput from 'airship/components/Validator/TextInput'
import {isRequired} from 'airship/components/Validator/validators'
import getContext from 'airship/modules/page-context'
import Button from 'airship/patterns/button'
import Feedback from 'airship/patterns/feedback'
import Loading from 'airship/patterns/Loading'
import Modal from 'airship/patterns/modal'
import RadioSelect, {Selectable} from 'airship/patterns/RadioSelect'
import fileToDataURI from 'airship/utils/fileToDataURI'

import AdditionalFields from './AdditionalFields/AdditionalFields'
import {
  IMAGE_LIMIT_SIZE,
  PRODUCTS_WITH_ADDITIONAL_FIELDS,
  UPLOAD_IMAGE_SIZE_ERROR_MESSAGE,
} from './constants'
import ContactSupportAccessDeniedError from './ContactSupportAccessDeniedError'
import Details from './details'
import detailsSlice from './detailsSlice'
import EditDetails from './editDetails'
import './InProductSupport.css'
import {productsSelect} from './products'
import {
  selectDetails,
  selectErrorMessage,
  selectIsUnauthorized,
  selectLoading,
} from './selectors'
import {useInProductSupportDispatch} from './store'
import ThankYou from './thankYou'
import {createTicketThunk} from './thunks'
import {Context, Urgency} from './types'

export interface Props {
  isOpen: boolean
  onClose: () => void
}

export interface DetailsData {
  appName: string
  url: string
  appKey: string
}

const ContactSupportModalFC: React.FC<Props> = ({isOpen, onClose}) => {
  const [showEditDetails, setShowEditDetails] = React.useState<boolean>(false)
  const [showThankYou, setShowThankYou] = React.useState<boolean>(false)
  const [imageData, setImageData] = React.useState<string>()
  const [imageName, setImageName] = React.useState<string>()
  const [isValid, setIsValid] = React.useState<boolean>()

  const context = getContext<Context>()

  const [subject, setSubject] = React.useState<string>()
  const [issueDescription, setIssueDescription] = React.useState<string>()
  const [urgency, setUrgency] = React.useState(Urgency.Low)
  const [product, setProduct] = React.useState<string>(
    context.project ? 'wallet' : ''
  )
  const [fieldError, setFieldError] = React.useState<string>('')

  const loading = useSelector(selectLoading)
  const errorMessage = useSelector(selectErrorMessage)
  const isUnauthorized = useSelector(selectIsUnauthorized)

  const dispatch = useInProductSupportDispatch()
  const stateDetails = useSelector(selectDetails)

  const urgencySelectorItems: Selectable[] = [
    {id: 'low', label: 'Low'},
    {id: 'medium', label: 'Medium'},
    {id: 'highest', label: 'High'},
    {id: 'urgent', label: 'Critical'},
  ]

  const handleSend = async (): Promise<void> => {
    const details = {
      ...stateDetails,
      subject,
      issueDescription,
      product,
      urgency,
    }

    dispatch(detailsSlice.actions.clearErrorMessage())

    const findProductLabel = productsSelect.find((prod) => product === prod.id)

    if (findProductLabel !== undefined) {
      details.productLabel = findProductLabel.label
    }

    if (imageData) details.image = imageData
    if (imageName) details.imageName = imageName

    setShowEditDetails(false)

    setFieldError('')
    const result = await dispatch(createTicketThunk(details))
    if (createTicketThunk.fulfilled.match(result)) {
      setShowThankYou(true)
      setImageData('')
      setImageName('')
    }
  }

  const handleClose = (): void => {
    setShowThankYou(false)
    setShowEditDetails(false)
    dispatch(detailsSlice.actions.clearFields())
    setImageData('')
    setImageName('')
    setFieldError('')
    onClose()
  }

  const onFileSelect = async (
    ev: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!ev.target.files) return

    const files: FileList = ev.target.files
    const file = files[0]

    if (!file) return

    const newImageData = await fileToDataURI(file)
    setImageData(newImageData)
    setImageName(file.name)

    if (file.size > IMAGE_LIMIT_SIZE) {
      setFieldError(UPLOAD_IMAGE_SIZE_ERROR_MESSAGE)
      return
    }
  }

  const onFileRemove = (): void => {
    setImageData('')
    setImageName('')
    if (fieldError === UPLOAD_IMAGE_SIZE_ERROR_MESSAGE) {
      setFieldError('')
    }
  }

  return (
    <>
      <Modal size="md" isOpen={isOpen} onClose={onClose}>
        {loading ? (
          <div className="u-lg-pad-6">
            <Loading centerInParent={true} />
          </div>
        ) : showThankYou ? (
          <ThankYou handleClose={handleClose} />
        ) : (
          <div>
            <h1 className="u-space-bottom-1">Contact Support</h1>
            {showEditDetails ? (
              <EditDetails setShowEditDetails={setShowEditDetails} />
            ) : (
              <Details setShowEditDetails={setShowEditDetails} />
            )}
            <h1 className="u-space-bottom-05">Where is your issue?</h1>
            <ValidationScope onValid={setIsValid}>
              <FieldValidation
                id="product"
                value={product}
                validators={[isRequired()]}
                asyncValidators={[]}
              >
                <Select
                  id="product"
                  displayClasses="u-space-bottom-05"
                  selected={product}
                  items={productsSelect}
                  onSelect={setProduct}
                  defaultItemText="Select product or feature"
                />
              </FieldValidation>

              {product !== '' &&
              PRODUCTS_WITH_ADDITIONAL_FIELDS.includes(product) ? (
                <AdditionalFields selected={product} />
              ) : null}

              <FieldValidation
                id="subject"
                value={subject}
                validators={[isRequired()]}
                asyncValidators={[]}
              >
                <h1 className="u-space-bottom-05">Subject</h1>
                <TextInput
                  placeholder="Briefly describe your issue"
                  displayClasses="u-space-bottom-05"
                  id="subject"
                  value={subject}
                  onChange={setSubject}
                />
              </FieldValidation>

              <h1 className="u-space-bottom-05">Issue</h1>
              <FieldValidation
                id="issueDescription"
                value={issueDescription}
                validators={[isRequired()]}
                asyncValidators={[]}
              >
                <TextareaInput
                  placeholder="Describe your issue in detail"
                  displayClasses="u-space-bottom-05"
                  id="issueDescription"
                  size="md"
                  value={issueDescription}
                  onChange={setIssueDescription}
                />
              </FieldValidation>

              <div className="u-flex">
                <h1>Attach screenshot</h1>
                <p className="u-space-left-05">(Optional)</p>
              </div>
              <p>
                Image must be a .jpg, .gif or .png file type with a size limit
                of {filesize(IMAGE_LIMIT_SIZE)}.
              </p>
              {!imageData && (
                <FileUpload
                  accept="image/jpeg,image/png,image/gif"
                  onChange={onFileSelect}
                  buttonText="Choose file"
                />
              )}
              {imageData && (
                <div className="u-flex u-space-bottom-1">
                  <img className="ips-thumbnail" src={imageData} alt="" />
                  <Button onClick={() => onFileRemove()}>remove</Button>
                </div>
              )}

              <h1 className="u-space-bottom-05">Urgency</h1>
              <RadioSelect
                displayClasses={
                  errorMessage || isUnauthorized
                    ? 'u-text-medium u-space-bottom-05'
                    : 'u-text-medium u-space-bottom-1'
                }
                items={urgencySelectorItems}
                selected={urgency}
                onSelect={(item) => setUrgency(item as Urgency)}
              />
            </ValidationScope>
            {errorMessage || fieldError ? (
              <Feedback displayClasses="u-space-bottom-1" type="error">
                {errorMessage ? errorMessage : fieldError}
              </Feedback>
            ) : null}

            {isUnauthorized ? <ContactSupportAccessDeniedError /> : null}

            <div className="u-flex u-flex-justify-end">
              <Button onClick={handleClose} displayClasses="u-space-right-05">
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                style="navy"
                displayClasses="u-border-none"
                disabled={!isValid || fieldError !== ''}
              >
                Contact Support
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default ContactSupportModalFC
